import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../api';
import AuthHeader from '../components/Header/AuthHeader';
import Button from '../components/Button';
import '@fortawesome/fontawesome-free/css/all.min.css';

type ResultStatus = 'success' | 'error' | null;

const EmailDogrula: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string>('');
  const [activationCode, setActivationCode] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [resultStatus, setResultStatus] = useState<ResultStatus>(null);

  const locationState = location.state as { email?: string; message?: string } | undefined;
  const redirectMessage = locationState?.message;

  useEffect(() => {
    if (locationState?.email && typeof locationState.email === 'string') {
      setEmail(locationState.email);
    }
  }, [location.state]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setResultStatus(null);

    const code = activationCode.trim();
    if (!email.trim()) {
      return;
    }
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await authService.activateEmail({
        email: email.trim(),
        activationCode: code,
      });
      setResultStatus(result.isSuccess ? 'success' : 'error');
    } catch {
      setResultStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTamamClick = (): void => {
    const wasSuccess = resultStatus === 'success';
    setResultStatus(null);
    if (wasSuccess) {
      navigate('/giris');
    }
  };

  const isCodeValid = /^\d{0,6}$/.test(activationCode);

  return (
    <>
      <AuthHeader />
      <div className="sb-page-bg min-vh-100 d-flex align-items-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h4 className="card-title text-center mb-4">
                    Email Adresini Doğrula
                  </h4>
                  <p className="text-muted text-center small mb-3">
                    Kayıt sonrası e-posta adresinize gelen 6 haneli kodu girin.
                  </p>
                  <p className="text-muted text-center small mb-4">
                    E-postayı göremiyorsanız spam veya gereksiz klasörünüzü de kontrol edin.
                  </p>

                  {redirectMessage && (
                    <div className="alert alert-danger py-2 mb-3" role="alert">
                      {redirectMessage}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="email">
                        E-posta
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="form-control"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ornek@email.com"
                        autoComplete="email"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label" htmlFor="activationCode">
                        Doğrulama Kodu (6 hane)
                      </label>
                      <input
                        id="activationCode"
                        type="text"
                        inputMode="numeric"
                        pattern="\d{6}"
                        maxLength={6}
                        className="form-control form-control-lg text-center"
                        placeholder="000000"
                        value={activationCode}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '');
                          if (v.length <= 6) setActivationCode(v);
                        }}
                        autoComplete="one-time-code"
                      />
                      <div className="form-text text-center">
                        6 haneli kodu girin
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={!email.trim() || activationCode.length !== 6 || !isCodeValid}
                      loading={submitting}
                    >
                      Doğrula
                    </Button>
                  </form>

                  <div className="text-center mt-3">
                    <a className="text-decoration-none small" href="/giris">
                      Giriş sayfasına dön
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Result modal */}
      {resultStatus && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content text-center p-4">
                <div className="modal-body py-4">
                  {resultStatus === 'success' ? (
                    <>
                      <div className="text-success mb-3">
                        <i className="fas fa-check-circle" style={{ fontSize: '4rem' }} />
                      </div>
                      <h5 className="mb-2">E-posta Adresiniz Doğrulandı</h5>
                      <p className="text-muted mb-0">
                        Hesabınız aktif edildi. Giriş yapabilirsiniz.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-danger mb-3">
                        <i className="fas fa-times-circle" style={{ fontSize: '4rem' }} />
                      </div>
                      <h5 className="mb-2">Doğrulama Başarısız</h5>
                      <p className="text-muted mb-0">
                        E-posta veya kod hatalı olabilir. Lütfen tekrar deneyin.
                      </p>
                    </>
                  )}
                </div>
                <div className="modal-footer border-0 justify-content-center pb-4">
                  <Button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleTamamClick}
                  >
                    Tamam
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};

export default EmailDogrula;
